import React, { useState, useEffect, useRef } from 'react';
import getTemplateProperties from '../../../lib/getJobTemplateProperties';
import importTemplate, {
  TemplateProperties, TemplatesProperty, TemplateDependencies,
} from '../../../lib/importJobTemplate';
import transformJob from '../../../lib/transformJob';
import Error from '../../Error';
import { useSiteContext } from '../../SiteContext';
import { JobQuery } from '../../../lib/graphql/api/graphql';

export interface JobTemplateProperties {
  job: NonNullable<JobQuery['job']> & {
    template: NonNullable<NonNullable<JobQuery['job']>['template']>;
  };
  locale: string;
}

interface TemplatesProperties {
  CompanyDescription?: React.ElementType<TemplateProperties>;
  Description?: React.ElementType<TemplateProperties>;
  properties?: Record<string, unknown>;
  [key: keyof TemplateDependencies]: TemplatesProperty;
}

const JobTemplate = ({
  job,
  locale,
}: JobTemplateProperties) => {
  const transformedJob = transformJob(job);
  const templateProperties = getTemplateProperties(
    transformedJob,
    locale,
  );
  const [templates, setTemplates] = useState<TemplatesProperties>({});
  const [error, setError] = useState<string | null>(null);
  const { origin } = useSiteContext();
  const {
    template: { sourceName, revision },
  } = job;
  const reference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function fetchTemplates() {
      try {
        const fetchedTemplates = await importTemplate(
          sourceName,
          revision,
          origin,
        );
        setTemplates(fetchedTemplates);
        setError(null);
      } catch (importError) {
        setTemplates({});
        setError(String(importError));
      }
    }

    fetchTemplates();
  }, [origin, revision, sourceName]);

  const { CompanyDescription, Description } = templates;

  return (
    <div ref={reference}>
      { error && <Error error="Error loading job details" /> }
      { CompanyDescription && <CompanyDescription {...templateProperties} /> }
      { Description && <Description {...templateProperties} /> }
    </div>
  );
};

export default JobTemplate;
